"use client";

import { useEffect } from "react";

export interface ModalProps {
  open: boolean;
  title: string;
  message?: React.ReactNode;
  // Confirm = primary action button (success-toned border). Always present.
  confirmLabel?: string;
  busyLabel?: string;
  // Cancel = secondary button (accent-toned border, signals destructive
  // intent without screaming). Omitted → notify-only modal with a single
  // confirm button.
  cancelLabel?: string;
  submitting?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
  titleId?: string;
  // Optional tooltip text (title attribute) for the confirm/cancel buttons —
  // additive so existing callers that don't pass them are unaffected. See
  // CLAUDE.md's button-tooltip hard rule; callers whose confirmLabel/
  // cancelLabel is not self-explanatory should set these.
  confirmTitle?: string;
  cancelTitle?: string;
  // Confirm stays visible but inert until the dialog's input is valid (e.g.
  // nothing selected yet). Additive, like the titles above.
  confirmDisabled?: boolean;
  // Wider panel (max-w-2xl, scrolls inside the viewport) for dialogs that
  // carry a form rather than a sentence, e.g. the student-report options.
  wide?: boolean;
}

// Shared confirm/notify modal. Used directly when fine-grained control over
// the busy state is needed (e.g. keep open during a network call); otherwise
// callers go through useModal() which wraps this with a Promise-based API.
export default function Modal({
  open,
  title,
  message,
  confirmLabel = "OK",
  busyLabel,
  cancelLabel,
  submitting = false,
  onConfirm,
  onCancel,
  titleId = "modal-title",
  confirmTitle,
  cancelTitle,
  confirmDisabled = false,
  wide = false,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (cancelLabel && onCancel) onCancel();
        else if (!submitting) onConfirm();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, cancelLabel, onCancel, onConfirm, submitting]);

  if (!open) return null;

  const handleBackdropClick = () => {
    if (submitting) return;
    if (cancelLabel && onCancel) onCancel();
    else onConfirm();
  };

  const showCancel = Boolean(cancelLabel) && Boolean(onCancel);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: "color-mix(in oklch, var(--color-text) 32%, transparent)",
      }}
      onClick={handleBackdropClick}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`rounded-lg w-full p-6 ${wide ? "max-w-2xl max-h-[90vh] overflow-y-auto" : "max-w-sm"}`}
        style={{
          background: "var(--color-bg-elevated)",
          border: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        <h2
          id={titleId}
          className="text-base font-semibold leading-snug text-[var(--color-text)]"
        >
          {title}
        </h2>
        {message ? (
          <div className="mt-3 text-sm text-[var(--color-text)] leading-relaxed whitespace-pre-line">
            {message}
          </div>
        ) : null}
        <div className="mt-6 flex flex-wrap justify-end gap-3">
          {showCancel ? (
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              title={cancelTitle}
              className="text-sm px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "transparent",
                color: "var(--color-text)",
                border: "1px solid var(--color-accent)",
              }}
            >
              {cancelLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting || confirmDisabled}
            title={confirmTitle}
            className="text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "transparent",
              color: "var(--color-text)",
              border: "1px solid var(--color-success)",
            }}
          >
            {submitting && busyLabel ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
